import {createAsyncThunk, createSlice, type PayloadAction} from "@reduxjs/toolkit";
import type {AuctionBid, CarAuction, CarItem} from "@/types";
import {keysToCamelCase, normalizeError} from "@/utils";
import type {RootState} from "@/store";
import {supabase} from "@/utils/supabase.ts";
import {editVehicleThunk} from "@/store/thunks/editVehicleThunk.ts";

export interface VehicleState {
    currentVehicle?: CarAuction | CarItem;
    bids: AuctionBid[];
    loading: boolean;
    hasError: boolean;
    errorMessage?: string;
}

const initialState: VehicleState = {
    bids: [],
    hasError: false,
    loading: false
};


export const setCurrentVehicleAsync = createAsyncThunk<
    CarAuction | CarItem,
    string,
    { state: RootState; rejectValue: string }
>(
    "vehicle/setCurrent",
    async (id, {getState, rejectWithValue}) => {
        const {auction, listing, authentication} = getState();

        try {
            if (!authentication.user) {
                return rejectWithValue('no user')
            }
            const cachedVehicle =
                auction.auctions.find((item) => item.id === id) ??
                listing.listings.find((item) => item.id === id);

            if (cachedVehicle) {
                return cachedVehicle;
            }

            const {data, error} = await supabase
                .from("vehicles")
                .select("*, auction:auctions(*), listing:listings(*)")
                .eq("id", id)
                .eq('seller_id', authentication.user.id)
                .single();

            if (error) {
                return rejectWithValue(error.message);
            }

            if (!data) {
                return rejectWithValue("Vehicle not found");
            }

            if (data.type === 'auction') {
                return keysToCamelCase<CarAuction>({
                    ...data.auction,
                    ...data
                })
            } else if (data.type === 'listing') {
                return keysToCamelCase<CarItem>({
                    ...data.listing,
                    ...data
                })
            } else {
                return rejectWithValue('Invalid vehicle type')
            }
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to fetch vehicle"));
        }
    }
);

export const togglePublish = createAsyncThunk<
    { id: string; published: boolean },
    { id: string; published?: boolean },
    { state: RootState; rejectValue: string }
>(
    "vehicle/togglePublish",
    async ({id, published}, {getState, rejectWithValue}) => {
        try {
            const {vehicle, authentication} = getState();
            if (!authentication.user) {
                return rejectWithValue("No authenticated dealer");
            }
            const currentVehicle = vehicle.currentVehicle;
            const nextPublished = published ?? !Boolean(currentVehicle?.published);

            const {data, error} = await supabase
                .from("vehicles")
                .update({published: nextPublished, active: nextPublished? 'active' : 'inactive'})
                .eq("id", id)
                .eq("seller_id", authentication.user.id)
                .select("id, published")
                .single();

            if (error) {
                return rejectWithValue(error.message);
            }

            return {
                id: String(data.id),
                published: Boolean(data.published)
            };
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to update publish state"));
        }
    }
);

export const updateStatus = createAsyncThunk<
    { id: string; active: NonNullable<CarItem["active"]> },
    { id: string; active: NonNullable<CarItem["active"]> },
    { state: RootState; rejectValue: string }
>(
    "vehicle/updateStatus",
    async ({id, active}, {getState, rejectWithValue}) => {
        try {
            const {vehicle, authentication} = getState();
            if (!authentication.user) {
                return rejectWithValue("No authenticated dealer");
            }
            const currentVehicle = vehicle.currentVehicle;

            const {error: vehicleError} = await supabase
                .from("vehicles")
                .update({active})
                .eq("id", id)
                .eq("seller_id", authentication.user.id);

            if (vehicleError) {
                return rejectWithValue(vehicleError.message);
            }

            if (currentVehicle?.type === "listing") {
                const {error: listingError} = await supabase
                    .from("listings")
                    .update({status: active})
                    .eq("vehicle_id", id);

                if (listingError) {
                    return rejectWithValue(listingError.message);
                }
            }

            if (currentVehicle?.type === "auction") {
                const auctionStatus =
                    active === "live"
                        ? "live"
                        : active === "sold"
                            ? "ended"
                            : "draft";

                const {error: auctionError} = await supabase
                    .from("auctions")
                    .update({status: auctionStatus})
                    .eq("vehicle_id", id);

                if (auctionError) {
                    return rejectWithValue(auctionError.message);
                }
            }

            return {id, active};
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to update vehicle status"));
        }
    }
);

export const vehicleSlice = createSlice({
    name: "vehicle",
    initialState,
    reducers: {
        clearCurrentVehicle(state) {
            state.currentVehicle = undefined;
            state.bids = [];
            state.hasError = false;
            state.errorMessage = undefined;
        },
        setCurrentVehicle(state, action: PayloadAction<CarAuction | CarItem | undefined>) {
            state.currentVehicle = action.payload;
            state.bids = action.payload?.type === "auction" ? action.payload.bids ?? [] : [];
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setCurrentVehicleAsync.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(setCurrentVehicleAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.currentVehicle = action.payload;
                state.bids = action.payload.type === "auction" ? action.payload.bids ?? [] : [];
            })
            .addCase(setCurrentVehicleAsync.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to fetch vehicle";
            })
            .addCase(togglePublish.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(togglePublish.fulfilled, (state, action) => {
                state.loading = false;
                if (state.currentVehicle?.id === action.payload.id) {
                    state.currentVehicle = {
                        ...state.currentVehicle,
                        published: action.payload.published
                    };
                }
            })
            .addCase(togglePublish.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to update publish state";
            })
            .addCase(updateStatus.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(updateStatus.fulfilled, (state, action) => {
                state.loading = false;
                if (state.currentVehicle?.id === action.payload.id) {
                    state.currentVehicle = {
                        ...state.currentVehicle,
                        active: action.payload.active
                    };
                }
            })
            .addCase(updateStatus.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to update vehicle status";
            })
            .addCase(editVehicleThunk.pending, (state) => {
                state.loading = true;
                state.hasError = false;
                state.errorMessage = undefined;
            })
            .addCase(editVehicleThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.currentVehicle = action.payload;
                state.bids = action.payload.type === "auction" ? action.payload.bids ?? [] : [];
            })
            .addCase(editVehicleThunk.rejected, (state, action) => {
                state.loading = false;
                state.hasError = true;
                state.errorMessage = (action.payload as string) ?? action.error.message ?? "Failed to update vehicle";
            });
    }
});

export const {clearCurrentVehicle, setCurrentVehicle} = vehicleSlice.actions;

export default vehicleSlice;
